import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  EmptyState,
  Input,
  Modal,
  useToast,
} from '@adop/ui';
import { Download, Pencil, Plus, Printer, Trash2 } from 'lucide-react';
import type { PairingCodeResponse } from '@adop/shared';
import { useWorkspace } from '../../lib/workspace-context';
import { useAsync } from '../../lib/useAsync';
import {
  insertPrintJob,
  listPrinterDevices,
  renamePrinterDevice,
  revokePrinterDevice,
  subscribeToLocation,
  type PrinterDeviceRow,
} from '../../lib/data';
import { apiFetch, errorMessage } from '../../lib/api';
import { isPrinterOnline, relativeTime } from '../../lib/format';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { ListSkeleton } from '../../components/LoadingState';

export function PrintersPage() {
  const { location, canManage } = useWorkspace();
  const { toast } = useToast();
  const state = useAsync(
    () => (location ? listPrinterDevices(location.id) : Promise.resolve([])),
    [location?.id],
  );
  const [pairing, setPairing] = useState<PairingCodeResponse | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<PrinterDeviceRow | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [revokeTarget, setRevokeTarget] = useState<PrinterDeviceRow | null>(null);

  useEffect(() => {
    if (!pairing) return;
    const tick = () => {
      const seconds = Math.max(0, Math.round((new Date(pairing.expires_at).getTime() - Date.now()) / 1000));
      setCountdown(seconds);
      if (seconds <= 0) setPairing(null);
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [pairing]);

  const reloadDevices = state.reload;

  useEffect(() => {
    if (!location) return;
    const channel = subscribeToLocation(location.id, ['printer_devices', 'print_jobs'], () =>
      reloadDevices(),
    );
    return () => {
      void channel.unsubscribe();
    };
  }, [location, reloadDevices]);

  const startPairing = async () => {
    if (!location) return;
    setBusy(true);
    setError(null);
    try {
      const response = await apiFetch<PairingCodeResponse>(
        `/api/platform/locations/${location.id}/pairing-code`,
        { method: 'POST' },
      );
      setPairing(response);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const testPrint = async (device: PrinterDeviceRow) => {
    if (!location) return;
    try {
      await insertPrintJob({
        restaurantId: location.restaurant_id,
        locationId: location.id,
        deviceId: device.id,
        kind: 'test',
      });
      toast({ title: `Test print queued to ${device.name}`, variant: 'success' });
    } catch (cause) {
      toast({ title: 'Could not queue test print', description: errorMessage(cause), variant: 'error' });
    }
  };

  const saveRename = async () => {
    if (!renameTarget) return;
    try {
      await renamePrinterDevice(renameTarget.id, renameValue.trim() || renameTarget.name);
      toast({ title: 'Printer renamed', variant: 'success' });
      setRenameTarget(null);
      state.reload();
    } catch (cause) {
      toast({ title: 'Could not rename', description: errorMessage(cause), variant: 'error' });
    }
  };

  const revoke = async () => {
    if (!revokeTarget) return;
    try {
      await revokePrinterDevice(revokeTarget.id);
      toast({ title: 'Printer revoked', variant: 'success' });
      setRevokeTarget(null);
      state.reload();
    } catch (cause) {
      toast({ title: 'Could not revoke', description: errorMessage(cause), variant: 'error' });
    }
  };

  if (!location) {
    return <EmptyState icon={<Printer className="h-6 w-6" />} title="Select a location" />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Printers"
        description="Pair thermal printers so orders print automatically in the kitchen."
        action={
          canManage ? (
            <Button size="sm" loading={busy} onClick={() => void startPairing()}>
              <Plus className="h-4 w-4" /> Pair a new printer
            </Button>
          ) : null
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      {state.loading ? (
        <ListSkeleton />
      ) : (state.data ?? []).length === 0 ? (
        <EmptyState
          icon={<Printer className="h-6 w-6" />}
          title="No printers paired"
          description="Pair a printer to start printing tickets automatically."
        />
      ) : (
        <div className="space-y-3">
          {(state.data ?? []).map((device) => {
            const online = !device.revoked_at && isPrinterOnline(device.last_seen_at);
            return (
              <Card key={device.id}>
                <CardHeader>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span
                        className={`h-2.5 w-2.5 rounded-full ${online ? 'bg-emerald-500' : 'bg-zinc-300'}`}
                        aria-hidden="true"
                      />
                      <CardTitle>{device.name}</CardTitle>
                      {device.revoked_at ? <Badge variant="danger">Revoked</Badge> : null}
                    </div>
                    {canManage && !device.revoked_at ? (
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => void testPrint(device)}>
                          Test print
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setRenameTarget(device);
                            setRenameValue(device.name);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setRevokeTarget(device)}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </CardHeader>
                <CardContent>
                  <dl className="grid gap-2 text-sm sm:grid-cols-4">
                    <div>
                      <dt className="text-xs text-zinc-500">Windows printer</dt>
                      <dd className="text-zinc-900">{device.printer_name}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-zinc-500">Paper</dt>
                      <dd className="text-zinc-900">{device.paper_width} mm</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-zinc-500">Last seen</dt>
                      <dd className="text-zinc-900">
                        {device.last_seen_at ? relativeTime(device.last_seen_at) : 'Never'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-zinc-500">App version</dt>
                      <dd className="text-zinc-900">{device.app_version ?? '—'}</dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        open={pairing !== null}
        onClose={() => setPairing(null)}
        title="Pair a new printer"
        size="sm"
        footer={
          <Button variant="outline" onClick={() => setPairing(null)}>
            Done
          </Button>
        }
      >
        {pairing ? (
          <div className="space-y-4 text-center">
            <p className="text-sm text-zinc-600">Enter this code in the adop printer app:</p>
            <p className="text-5xl font-bold tracking-[0.3em] text-zinc-900">{pairing.code}</p>
            <p className="text-sm text-zinc-500">
              Expires in {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}
            </p>
            <a
              href={pairing.download_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-zinc-900 underline"
            >
              <Download className="h-4 w-4" /> Download the printer app
            </a>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={renameTarget !== null}
        onClose={() => setRenameTarget(null)}
        title="Rename printer"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>
              Cancel
            </Button>
            <Button onClick={() => void saveRename()}>Save</Button>
          </>
        }
      >
        <Input label="Name" value={renameValue} onChange={(event) => setRenameValue(event.target.value)} />
      </Modal>

      <ConfirmDialog
        open={revokeTarget !== null}
        title={`Revoke ${revokeTarget?.name ?? ''}?`}
        message="The printer will stop receiving jobs until you pair it again."
        confirmLabel="Revoke"
        variant="danger"
        onConfirm={() => void revoke()}
        onCancel={() => setRevokeTarget(null)}
      />
    </div>
  );
}
